import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Column, usePagination, useSortBy, useTable } from 'react-table';
import { TimeRangeContext } from '../../../context/TimeRangeContext';
import { buildRedirectQuery, buildApiQuery } from '../../../helpers/timeRangeUtils';
import api from '../../../api/Base';
import Helper from '../../../helpers/Helper';
import { getDefaultPageSize, getToolTipPosition } from '../../../helpers/utils';
import Alert from '../../../component/Alert';
import ReactTable from '../../../component/ReactTable';
import StatementToolTip from '../../../component/instance/table/StatementToolTip';
import TableCSVExport from '../../../component/TableCSVExport';
import SearchInput from '../../../component/SearchInput';
import { CHART_COLOURS_STATEMENTS } from '../../../component/Constants';
import { INSTANCE_TYPE_ORACLE, INSTANCE_TYPE_POSTGRES, INSTANCE_TYPE_SQLSERVER } from '../../../component/Constants';
import InstanceTarget from '../../../types/instance/InstanceTarget';
import Statement from '../../../types/instance/Statement';
import StatementsDataTable from '../../../types/instance/tables/StatementsDataTable';

type TagGroupStatementRow = StatementsDataTable & { time?: number; weight?: number; instanceIds: number[] };

interface TagGroupStatementsTableProps {
    tagName: string;
    tagValue: string;
    instances: InstanceTarget[];
    instanceTime: number;
}

const TagGroupStatementsTable: React.FC<TagGroupStatementsTableProps> = ({
    tagName,
    tagValue,
    instances,
    instanceTime
}) => {
    const { timeRange } = useContext(TimeRangeContext);
    const [statementsDataTable, setStatementsDataTable] = useState<TagGroupStatementRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [tableFilter, setTableFilter] = useState('');
    const [visibleToolTip, setVisibleToolTip] = useState({ id: '', position: 0 });

    const firstInstance = instances[0];
    const tagFilter = `${tagName}:${tagValue}`;
    const instanceMap = useMemo(() => {
        const map = new Map<number, InstanceTarget>();
        instances.forEach(inst => map.set(inst.id, inst));
        return map;
    }, [instances]);

    useEffect(() => {
        if (!tagName || !tagValue || instances.length === 0) {
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const tagParam = `tag=${encodeURIComponent(tagFilter)}`;

                // Fetch statements per-instance so we know which instance(s) each statement belongs to
                const perInstanceCalls = instances.map(inst =>
                    api.get(`activity/sql?limit=${process.env.REACT_APP_API_LIMIT}&${buildApiQuery(timeRange)}&statistic=executions&sort=waittime+desc&id=${inst.id}&${tagParam}`)
                        .then(res => ({ instanceId: inst.id, data: res.data || [] }))
                        .catch(() => ({ instanceId: inst.id, data: [] }))
                );
                const perInstanceResults = await Promise.all(perInstanceCalls);

                // Merge: group by sqlhash, sum metrics, collect instanceIds
                const merged = new Map<string, { item: any; instanceIds: number[] }>();
                for (const { instanceId, data } of perInstanceResults) {
                    for (const item of data) {
                        const existing = merged.get(item.sqlhash);
                        if (existing) {
                            existing.item.waittime = (existing.item.waittime ?? 0) + (item.waittime ?? 0);
                            const existingCount = existing.item.waitcount ?? existing.item.executions ?? 0;
                            const newCount = item.waitcount ?? item.executions ?? 0;
                            if (existing.item.waitcount !== undefined) {
                                existing.item.waitcount = existingCount + newCount;
                            } else {
                                existing.item.executions = existingCount + newCount;
                            }
                            existing.item.batchexecutions = (existing.item.batchexecutions ?? 0) + (item.batchexecutions ?? 0);
                            if (!existing.instanceIds.includes(instanceId)) {
                                existing.instanceIds.push(instanceId);
                            }
                        } else {
                            merged.set(item.sqlhash, { item: { ...item }, instanceIds: [instanceId] });
                        }
                    }
                }

                // Sort by waittime desc and build rows
                const sortedEntries = Array.from(merged.values()).sort((a, b) => (b.item.waittime ?? 0) - (a.item.waittime ?? 0));
                const statementsData = sortedEntries.map(({ item, instanceIds }, index) => {
                    const count = item.waitcount ?? item.executions;
                    const executions = count !== undefined && count !== null ? Number(count) : null;
                    const waittime = item.waittime ?? 0;
                    const averagetime = executions && executions > 0 ? waittime / executions : null;
                    const statement: Statement = {
                        database: item.database || '',
                        color: CHART_COLOURS_STATEMENTS[index % CHART_COLOURS_STATEMENTS.length],
                        sqlhash: item.sqlhash,
                        sqlid: item.sqlid || '',
                        sqltext: item.sqltext || '',
                        username: item.username || '',
                        batchexecutions: item.batchexecutions ?? 0,
                        executions: executions ?? 0,
                        waittime,
                        batchsqlhash: item.batchsqlhash || ''
                    };
                    return {
                        id: index,
                        color: statement.color,
                        sqlhash: item.sqlhash,
                        sqltext: (item.sqltext || '').slice(0, 1000),
                        executions,
                        waittime,
                        averagetime: averagetime != null ? Number(averagetime.toFixed(2)) : null,
                        statement,
                        time: waittime,
                        instanceIds
                    };
                });

                setStatementsDataTable(statementsData);
            } catch (error) {
                console.error('Error fetching tag group data:', error);
                setStatementsDataTable([]);
            } finally {
                setLoading(false);
            }
        };

        void fetchData();
    }, [tagName, tagValue, tagFilter, instances, timeRange]);

    const data = useMemo(() =>
        statementsDataTable.filter(row =>
            (row.sqlhash || '').toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.sqltext || '').toLowerCase().includes(tableFilter.toLowerCase()) ||
            (row.statement?.sqlid || '').toLowerCase().includes(tableFilter.toLowerCase())
        ),
        [statementsDataTable, tableFilter]
    );

    const hiddenColumns = useMemo(() => {
        if (!firstInstance) return ['statement', 'id', 'color'];
        switch (firstInstance.type) {
            case INSTANCE_TYPE_SQLSERVER:
            case INSTANCE_TYPE_ORACLE:
                return ['statement', 'id', 'color', 'executions', 'averagetime'];
            case INSTANCE_TYPE_POSTGRES:
                return firstInstance.use_pg_stat_statements ? ['statement', 'id', 'color'] : ['statement', 'id', 'color', 'executions', 'averagetime'];
            default:
                return ['statement', 'id', 'color', 'executions', 'averagetime'];
        }
    }, [firstInstance]);

    const [openDropdownHash, setOpenDropdownHash] = useState<string | null>(null);
    const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown on outside click or scroll
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenDropdownHash(null);
            }
        };
        const handleScroll = () => setOpenDropdownHash(null);
        if (openDropdownHash) {
            document.addEventListener('mousedown', handleClickOutside);
            window.addEventListener('scroll', handleScroll, true);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openDropdownHash]);

    const getStatementLink = (instanceId: number, sqlhash: string) =>
        `/instances/${instanceId}/activity/statement/${sqlhash}/text?${buildRedirectQuery(timeRange)}`;

    const renderInstanceLink = (sqlhash: string, rowInstanceIds: number[], children: React.ReactNode) => {
        if (!rowInstanceIds || rowInstanceIds.length === 0) return <React.Fragment>{children}</React.Fragment>;

        if (rowInstanceIds.length === 1) {
            return <Link to={getStatementLink(rowInstanceIds[0], sqlhash)}>{children}</Link>;
        }

        // Multiple instances — show dropdown
        const isOpen = openDropdownHash === sqlhash;
        return (
            <React.Fragment>
                <span
                    role="button"
                    className="text-primary"
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (isOpen) {
                            setOpenDropdownHash(null);
                        } else {
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setDropdownPos({ top: rect.top - 2, left: rect.left });
                            setOpenDropdownHash(sqlhash);
                        }
                    }}
                >
                    {children} <i className="fal fa-chevron-down fa-xs ms-1" />
                </span>
                {isOpen && (
                    <div
                        ref={dropdownRef}
                        className="dropdown-menu show"
                        style={{
                            position: 'fixed',
                            bottom: window.innerHeight - dropdownPos.top,
                            left: dropdownPos.left,
                            minWidth: '200px',
                            zIndex: 1050
                        }}
                    >
                        <div className="dropdown-header">Select instance</div>
                        {rowInstanceIds.map(instId => {
                            const inst = instanceMap.get(instId);
                            return (
                                <Link
                                    key={instId}
                                    className="dropdown-item"
                                    to={getStatementLink(instId, sqlhash)}
                                    onClick={() => setOpenDropdownHash(null)}
                                >
                                    <i className="fal fa-database fa-fw me-1" />
                                    {inst?.name ?? `Instance ${instId}`}
                                </Link>
                            );
                        })}
                    </div>
                )}
            </React.Fragment>
        );
    };

    const columns: Column<TagGroupStatementRow>[] = useMemo(() => [
        { accessor: 'statement', disableSortBy: true },
        { accessor: 'color', disableSortBy: true },
        { accessor: 'id', disableSortBy: true },
        {
            Header: '#',
            accessor: 'sqlhash',
            className: 'concatenate-small col-width-10',
            defaultCanSort: true,
            sortType: (rowA: any, rowB: any, columnId: string) => {
                const a = Number(rowA.values[columnId]);
                const b = Number(rowB.values[columnId]);
                return a - b;
            },
            Cell: ({ cell }: any) => (
                <React.Fragment>
                    <span className="me-1 ps-1" style={{ backgroundColor: cell.row.values.color }}>&nbsp;</span>
                    <i className="fal fa-arrow-up-right-from-square fa-fw" data-tooltip-id="global-tooltip" data-tooltip-content="Open in new page" /> {cell.row.values.sqlhash}
                </React.Fragment>
            )
        },
        {
            Header: 'Statement',
            accessor: 'sqltext',
            className: 'concatenate',
            disableSortBy: true,
            Cell: ({ cell }: any) => {
                if (!cell.row.values.sqltext) return <React.Fragment>-</React.Fragment>;
                return <React.Fragment>{cell.row.values.sqltext.substring(0, 200)}</React.Fragment>;
            }
        },
        {
            Header: 'Executions',
            accessor: 'executions',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }: any) => (
                cell.row.values.executions != null && cell.row.values.executions !== 0
                    ? Helper.getFormattedNumber(cell.row.values.executions)
                    : <React.Fragment>-</React.Fragment>
            )
        },
        {
            Header: 'Average Time',
            accessor: 'averagetime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }: any) => (
                cell.row.values.averagetime != null && cell.row.values.executions != null && cell.row.values.executions > 0
                    ? Helper.getTimeInEnglish(cell.row.values.averagetime)
                    : <React.Fragment>-</React.Fragment>
            )
        },
        {
            Header: 'Total Time',
            accessor: 'waittime',
            headerClassName: 'text-end',
            className: 'text-end col-width-5 text-nowrap',
            defaultCanSort: true,
            sortType: (rowA: any, rowB: any, columnId: string) => {
                const aWait = Number(rowA.values[columnId]) || 0;
                const bWait = Number(rowB.values[columnId]) || 0;
                if (aWait !== bWait) return bWait - aWait;
                const aHash = Number(rowA.values['sqlhash']) || 0;
                const bHash = Number(rowB.values['sqlhash']) || 0;
                return aHash - bHash;
            },
            Cell: ({ cell }: any) => (
                cell.row.values.waittime != null
                    ? Helper.getTimeInEnglish(cell.row.values.waittime)
                    : <React.Fragment>-</React.Fragment>
            )
        },
        {
            Header: 'Weight %',
            accessor: 'weight',
            headerClassName: 'text-end',
            className: 'text-end col-width-5',
            defaultCanSort: true,
            sortType: 'basic',
            Cell: ({ cell }: any) => {
                const { waittime } = cell.row.values;
                const percentage = instanceTime ? (100 / instanceTime) * waittime : 0;
                return <React.Fragment>{instanceTime ? `${Helper.formatPercentage(percentage)}%` : '-'}</React.Fragment>;
            }
        }
    ], [firstInstance, timeRange, instanceTime, tagFilter, data]);

    const tableInstance = useTable(
        {
            columns,
            data,
            initialState: {
                hiddenColumns,
                pageIndex: 0,
                pageSize: getDefaultPageSize()
            }
        },
        useSortBy,
        usePagination
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = tableInstance;

    function clearTableFilter() {
        setTableFilter('');
    }

    return (
        <div className="card mb-4">
            <div className="card-header">
                <i className="fal fa-scroll fa-fw" aria-hidden="true" />
                Statements (tag: {tagName}={tagValue})
                <i
                    className="collapse-toggle"
                    role="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseTagGroupStatements"
                    aria-expanded="false"
                    aria-controls="collapseTagGroupStatements"
                />
            </div>
            <div id="collapseTagGroupStatements" className="card-body collapse show">
                {Number(process.env.REACT_APP_API_LIMIT) - 1 === data.length - 1 && (
                    <Alert
                        message={`To help preserve performance, we limit the total number of statement records below to the top ${Number(process.env.REACT_APP_API_LIMIT) - 1} in descending order by their total wait time.`}
                        heading="Statement records"
                        variant="alert-info"
                    />
                )}
                <div className="row row-cols-1 row-cols-md-2 table-search">
                    <div className="col col-md-10">
                        <TableCSVExport
                            tableInstance={tableInstance}
                            filename="Statements"
                            instanceTime={instanceTime}
                        />
                    </div>
                    <div className="col col-md-2">
                        <SearchInput
                            value={tableFilter}
                            onChange={(e) => setTableFilter(e.target.value)}
                            onClear={clearTableFilter}
                        />
                    </div>
                </div>
                <ReactTable
                    getTableProps={getTableProps}
                    getTableBodyProps={getTableBodyProps}
                    headerGroups={headerGroups}
                    prepareRow={prepareRow}
                    page={page}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    setPageSize={setPageSize}
                    pageIndex={pageIndex}
                    pageSize={pageSize}
                    dataLength={data.length}
                    error={null}
                    loading={loading === true}
                    renderCell={(cell) => {
                        if (cell.column.id === 'sqlhash') {
                            const rowInstanceIds: number[] = cell.row.original?.instanceIds || [];
                            return renderInstanceLink(
                                cell.row.values.sqlhash,
                                rowInstanceIds,
                                cell.render('Cell')
                            );
                        }
                        if (cell.column.id === 'sqltext') {
                            const rowInstanceIds: number[] = cell.row.original?.instanceIds || [];
                            const tooltipInstanceId = rowInstanceIds[0] ?? firstInstance?.id;
                            const tooltipInstance = tooltipInstanceId ? instanceMap.get(tooltipInstanceId) : firstInstance;
                            const cellContent = rowInstanceIds.length === 1
                                ? <Link to={getStatementLink(rowInstanceIds[0], cell.row.values.sqlhash)}>{cell.render('Cell')}</Link>
                                : cell.render('Cell');
                            return (
                                <div
                                    className="tooltip-scroll ellipsis"
                                    onMouseEnter={(event) =>
                                        setVisibleToolTip({
                                            id: cell.row.values.sqlhash,
                                            position: getToolTipPosition(event)
                                        })
                                    }
                                    onMouseLeave={() => setVisibleToolTip({ id: '', position: 0 })}
                                >
                                    {cellContent}
                                    {tooltipInstance && cell.row.values.sqlhash === visibleToolTip.id && (
                                        <StatementToolTip
                                            instanceType={tooltipInstance.type}
                                            position={visibleToolTip.position}
                                            id={cell.row.values.sqlhash}
                                            link={getStatementLink(tooltipInstance.id, cell.row.values.sqlhash)}
                                            cell={cell}
                                        />
                                    )}
                                </div>
                            );
                        }
                        return cell.render('Cell');
                    }}
                />
            </div>
        </div>
    );
};

export default TagGroupStatementsTable;
